<template>
  <div class="gift-animation-overlay" v-show="isPlaying">
    <canvas ref="riveCanvas" width="400" height="400"></canvas>
  </div>
</template>

<script>
import { Rive, Fit, Alignment } from "@rive-app/webgl";

export default {
  name: 'GiftAnimationOverlay',
  data() {
    return {
      riveInstance: null,
      isLoading: false,
      isPlaying: false,
    };
  },
  methods: {
    play(fileName) {
      if (!fileName) {
        console.warn("[GiftAnimationOverlay] Play called with no filename.");
        return;
      }
      
      // If already playing, clean up first.
      if (this.isPlaying) {
        console.warn("[GiftAnimationOverlay] Received new play request while already playing. Cleaning up previous.");
        this.cleanupRive(); 
      }

      // Set flags 
      this.isPlaying = true;
      this.isLoading = true;

      // REMOVED $nextTick wrapper
      const canvas = this.$refs.riveCanvas;
      if (!canvas) {
        console.error("[GiftAnimationOverlay] Canvas element not found when trying to play.");
        this.isLoading = false;
        this.isPlaying = false;
        return;
      }

      // Clean up robustly RIGHT BEFORE creating the new instance
      this.cleanupRive(); 
      
      try {
        // Capture the instance being created
        const newInstance = new Rive({
          // USER SPECIFIED PATH - DO NOT CHANGE
          src: `../../assets/animations/${fileName}`,
          canvas: canvas,
          autoplay: true,
          fit: Fit.Contain,
          alignment: Alignment.Center,
          onLoad: () => {
            this.isLoading = false;
            // Assign the successfully loaded instance to the component's data property
            this.riveInstance = newInstance;
          },
          onLoadError: (err) => {
            console.error(`[GiftAnimationOverlay] Rive load error for ${fileName}:`, err);
            this.isLoading = false;
            this.isPlaying = false;
            // If load fails, ensure the potentially partially created instance is not assigned
            if (this.riveInstance === newInstance) {
               this.riveInstance = null;
            }
          },
          onStop: () => {
            // Only set isPlaying to false if the instance that stopped is the CURRENT active instance
            if (this.riveInstance === newInstance) { 
              this.isPlaying = false; 
              // Optionally, perform final cleanup here if needed, though cleanupRive should handle it
              // this.cleanupRive(); // Maybe redundant if play is called again quickly
            } else {
              // console.log(`[GiftAnimationOverlay] Ignored onStop because the stopped instance is not the current one.`);
            }
          },
          onPlayError: (err) => {
            console.error(`[GiftAnimationOverlay] Rive play error for ${fileName}:`, err);
            this.isLoading = false;
            this.isPlaying = false;
             // If play fails, ensure the potentially partially created instance is not assigned
            if (this.riveInstance === newInstance) {
               this.riveInstance = null;
            }
          },
        });
        // NOTE: We assign this.riveInstance inside onLoad now, not here.
      } catch(riveError) {
        console.error(`[GiftAnimationOverlay] Failed to instantiate Rive for ${fileName}:`, riveError);
        this.isLoading = false;
        this.isPlaying = false;
        // Ensure instance is null on catch
        this.riveInstance = null;
      }
      // END of removed $nextTick content
    },
    cleanupRive() {
      const instanceToCleanup = this.riveInstance; // Keep a reference to the instance we ARE cleaning up
      
      // Immediately nullify the main instance reference.
      // This should prevent late onStop callbacks from interfering with the *next* animation.
      this.riveInstance = null; 
      this.isLoading = false; // Reset loading state as well

      if (instanceToCleanup) {
        instanceToCleanup.stop();
        // Attempt cleanup if function exists
        if (typeof instanceToCleanup.cleanup === 'function') {
           instanceToCleanup.cleanup();
        } else {
          console.warn('[GiftAnimationOverlay] instanceToCleanup.cleanup function not found.');
        }
      } else {
        //console.log('[GiftAnimationOverlay] No instanceToCleanup found, skipping stop/cleanup calls.');
      }
    },
  },
  beforeDestroy() {
    this.cleanupRive();
  },
};
</script>

<style lang="scss" scoped>
.gift-animation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  pointer-events: none;

  canvas {
    max-width: 90%;
    max-height: 90%;
  }
}
</style>